import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MuloNavLink } from '@exl-ng/mulo-common/public-api';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { I18nService } from '../../core/i18n.service';
import { JwtUtilService } from '../../core/jwt-util.service';
import { Profile } from '../../shared/configurations/profile.constant';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import { ResearcherActivityDetails } from '../../shared/interfaces/activity.interface';
import {
    ListDataParameters,
    PageListData,
    ResearchFacetValue,
    SelectedFacet,
} from '../../shared/interfaces/list-data-params';
import { UrlUtils } from '../../shared/utils/url.utils';
import { ProfileService } from '../profile.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';

@Injectable({
    providedIn: 'root',
})
export class ProfileActivitiesService {
    private http = inject(HttpClient);
    private jwtUtilService = inject(JwtUtilService);
    private profileService = inject(ProfileService);
    private translate = inject(TranslateService);
    private i18nService = inject(I18nService);
    private configurationHandlerService = inject(ConfigurationHandlerService);

    institutionCode: string;
    selectedIndex$ = new BehaviorSubject<number>(0);
    facetsChange$ = new Subject<void>();
    facets: ResearchFacetValue[] = [];
    facetsFlatten: { [key: string]: ResearchFacetValue };
    facetsForMobile: MuloNavLink[] = [];
    countFacets = 0;

    chosenFacet;

    allActivitiesFacetsCount: number;

    constructor() {
        this.institutionCode = UrlUtils.getParam('institution');
    }

    getListDataParameters(
        newFacets?: { [key: string]: SelectedFacet[] },
        offset?: number,
    ) {
        const defaultListDataParams: ListDataParameters = {
            pagination: { rows: Profile.BULK_SIZE, offset },
            facets: newFacets,
            filters: {
                profile_visibility: 'true',
            },
            search: {
                searchBy: this.profileService.isPublicPage
                    ? 'profile_displayed_researchers_ids'
                    : 'researchers_ids',
                searchText: this.jwtUtilService.getUserID(),
            },
            sorts: [{ sortBy: 'start_date', sortDir: 'desc' }],
        };
        return defaultListDataParams;
    }

    displayActivitiesTabs(facets?: { [key: string]: SelectedFacet[] }) {
        let params = new HttpParams()
            .set('isUserLoggedIn', String(this.jwtUtilService.isLoggedIn()))
            .set('isPublicProfile', String(this.profileService.isPublicPage))
            .set('institution', this.institutionCode);
        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
        }
        return this.http.post(
            RestApiBase.ActivityApi.DISPLAY_ACTIVITIES_TAB,
            this.getListDataParameters(facets),
            { params },
        );
    }

    getFacetsActivities(activityKey?: string) {
        const facets = this.getCategoriesFacetsByTabConfiguration(activityKey);
        let params = new HttpParams().set('institution', this.institutionCode);
        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
        }
        this.http
            .post(
                RestApiBase.ActivityApi.FACETS_ACTIVITIES,
                this.getListDataParameters(facets),
                { params },
            )
            .subscribe((result: ResearchFacetValue[]) => {
                if (result?.length > 0 && result[0].count > 0) {
                    this.allActivitiesFacetsCount = result[0].count;
                    this.saveFacet(result);
                }
            });
    }

    private getCategoriesFacetsByTabConfiguration(activityKey: string) {
        const categories = activityKey
            ? this.configurationHandlerService
                  .activityDisplayTabConfiguration()
                  .find((tab) => tab.code == activityKey)?.categories
            : null;
        const facets = {
            ['category_facet']: categories?.map((category) => {
                return { key: category, isInclude: true };
            }),
        };
        return facets;
    }

    getAllActivities(facet: string, offset: number, activityKey?: string) {
        let params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('userLang', this.i18nService.getLanguage());
        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
        }

        const facets = {
            ...this.getCategoriesFacetsByTabConfiguration(activityKey),
            ...this.getFacetChoosen(facet),
        };
        return this.http.post<PageListData<ResearcherActivityDetails>>(
            RestApiBase.ActivityApi.ALL_ACTIVITIES + '/query',
            this.getListDataParameters(facets, offset),

            { params },
        );
    }

    getFacetChoosen(facet: string) {
        const currFacet = this.facetsFlatten[facet];
        const _facet = {};
        if (currFacet && facet !== 'all') {
            const facetValue: SelectedFacet = {
                key: currFacet.value,
                isInclude: true,
            };
            _facet[currFacet.facetKey] = [facetValue];
        }
        return _facet;
    }

    saveFacet(results_facets: ResearchFacetValue[]) {
        this.facets = results_facets;
        this.setFlattenArray();
        this.parseFacetsForMobile();
        this.facetsChange$.next();
    }

    setFlattenArray() {
        this.facetsFlatten = {};
        const cb = (facet: ResearchFacetValue) => {
            this.countFacets++;
            facet.index = this.countFacets;
            this.facetsFlatten[facet.value] = facet;
            facet.children && facet.children.forEach(cb);
        };
        this.facets.forEach(cb);
    }

    parseFacetsForMobile() {
        if (this.facets) {
            this.facets.forEach((facet) =>
                this.facetsForMobile.push(
                    this.parseFacetForMobileRecursion(facet, '', 0),
                ),
            );
        }
    }

    parseFacetForMobileRecursion(
        facet: ResearchFacetValue,
        parentCode: string,
        level,
    ) {
        const muloFacetResult = {} as MuloNavLink;
        muloFacetResult.name = facet.value;
        this.translate.get(facet.value).subscribe((res) => {
            muloFacetResult.label = res;
        });
        muloFacetResult.length = facet.count;
        muloFacetResult.level = level;
        muloFacetResult.id = this.facetsFlatten[facet.value].index;
        if (parentCode) {
            muloFacetResult.parent = parentCode;
        }

        if (facet.children?.length) {
            muloFacetResult.children = [];
            facet.children.forEach((subCategory) =>
                muloFacetResult.children.push(
                    this.parseFacetForMobileRecursion(
                        subCategory,
                        muloFacetResult.name,
                        level + 1,
                    ),
                ),
            );
        }

        return muloFacetResult;
    }

    deleteActivity(activity: ResearcherActivityDetails) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const activityId = activity.details.id;
        return this.http.delete(
            RestApiBase.ActivityApi.DELETE_ACTIVITY + '/' + activityId,
            { params },
        );
    }

    updatePublicProfileVisibility(visibility: boolean, activityId: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this.http.put(
            `${RestApiBase.ActivityApi.ALL_ACTIVITIES}/${activityId}/updatePublicProfileVisibility`,
            { userId: this.jwtUtilService.getUserID(), visibility },
            { params },
        );
    }
}
